import {clamp} from 'lodash';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import useLocalStorage from '@/hooks/use-local-storage';

export const CREATOR_SIDE_PANEL_WIDTH_STORAGE_KEY = 'creator-side-panel-width';
export const CREATOR_SIDE_PANEL_DEFAULT_WIDTH = 440;
export const CREATOR_SIDE_PANEL_MIN_WIDTH = 360;
export const CREATOR_SIDE_PANEL_MAX_WIDTH = 640;

export function sanitizeDrawerWidth(
  value: unknown,
  defaultWidth: number,
  minWidth: number,
  maxWidth: number
): number {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) {
    return defaultWidth;
  }
  return clamp(Math.round(numeric), minWidth, maxWidth);
}

type UseResizableDrawerWidthOptions = {
  storageKey?: string;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
};

function useResizableDrawerWidth({
  storageKey = CREATOR_SIDE_PANEL_WIDTH_STORAGE_KEY,
  defaultWidth = CREATOR_SIDE_PANEL_DEFAULT_WIDTH,
  minWidth = CREATOR_SIDE_PANEL_MIN_WIDTH,
  maxWidth = CREATOR_SIDE_PANEL_MAX_WIDTH,
}: UseResizableDrawerWidthOptions = {}) {
  const [storedWidth, setStoredWidth] = useLocalStorage<number>(
    storageKey,
    defaultWidth
  );
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  const dragCleanupRef = useRef<(() => void) | null>(null);

  const width = sanitizeDrawerWidth(
    dragWidth ?? storedWidth,
    defaultWidth,
    minWidth,
    maxWidth
  );

  useEffect(() => {
    return () => {
      dragCleanupRef.current?.();
    };
  }, []);

  const handleResizePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (event.button !== 0) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();

      dragCleanupRef.current?.();

      const previousUserSelect = document.body.style.userSelect;
      const previousCursor = document.body.style.cursor;
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';

      let latestWidth = sanitizeDrawerWidth(
        window.innerWidth - event.clientX,
        defaultWidth,
        minWidth,
        maxWidth
      );

      const updateWidth = (clientX: number) => {
        const nextWidth = sanitizeDrawerWidth(
          window.innerWidth - clientX,
          defaultWidth,
          minWidth,
          maxWidth
        );
        latestWidth = nextWidth;
        setDragWidth(nextWidth);
      };

      const endDrag = () => {
        document.body.style.userSelect = previousUserSelect;
        document.body.style.cursor = previousCursor;
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', endDrag);
        document.removeEventListener('pointercancel', endDrag);
        dragCleanupRef.current = null;
        setStoredWidth(latestWidth);
        setDragWidth(null);
      };

      const handlePointerMove = (moveEvent: PointerEvent) => {
        updateWidth(moveEvent.clientX);
      };

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', endDrag);
      document.addEventListener('pointercancel', endDrag);
      dragCleanupRef.current = endDrag;
      updateWidth(event.clientX);
    },
    [defaultWidth, minWidth, maxWidth, setStoredWidth]
  );

  return {width, handleResizePointerDown};
}

export default useResizableDrawerWidth;
